import { Component, ElementRef, HostListener, Injector, OnInit, ViewChild } from '@angular/core';
import { NgRedux, Select, Select$ } from '@angular-redux3/store';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SwUpdate } from '@angular/service-worker';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { MiniPlayerComponent } from './containers/mini-player/mini-player.component';
import { overlayRefToken } from './utils';
import { OfflineService } from './services/offline.service';
import { environment } from '../environments/environment';
import { APing, PlaylistRemoteAppState } from './store';
import { Media } from './store/entities';
import { UpdateRefreshDialogComponent } from './components/dialogs/update-refresh-dialog/update-refresh-dialog.component';

@Component({
  selector: 'mwo-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  protected isLoaded: boolean;

  constructor(
    translate: TranslateService,
    public dialog: MatDialog,
    private updates: SwUpdate,
    private router: Router,
    private overlay: Overlay,
    private injector: Injector,
    private offlineService: OfflineService,
    private ngRedux: NgRedux<PlaylistRemoteAppState>,
  ) {

    updates.available.subscribe(() => {
      this.dialogRef = this.dialog.open(UpdateRefreshDialogComponent, {
        data: {
          title: translate.get('dialog.app-updated'),
          refreshButton: translate.get('dialog.reload'),
          dialogRef: this.dialogRef,
          updates: this.updates,
        },
        disableClose: true,
      });
    });
  }

  get hasLoggedInRemote$(): Observable<boolean> {
    return combineLatest([this.hasRemote$, this.isLoggedIn$, this.currentMedia$.pipe(map(media => !(!media)))])
      .pipe(
        map(([isLoggedIn, hasRemote, currentMedia]) => (isLoggedIn && (hasRemote || environment.enableDesktopMode && currentMedia)))
      );
  }
  @Select$(['remote', 'id'], obs$ => obs$.pipe(map(Boolean))) hasRemote$: Observable<boolean>;
  @Select(['user', 'loggedIn']) isLoggedIn$: Observable<boolean>;
  @Select(['user', 'message']) message$: Observable<string>;
  @Select(['player', 'media']) currentMedia$: Observable<Media>;
  isMiniPlayerOpen = false;
  isLoggedIn = false;
  dialogRef: MatDialogRef<UpdateRefreshDialogComponent> = undefined;
  public backgroundImage: string | undefined;
  coverUrl = 'assets/icons/default_icon.webp';

  readonly appPages = [
    { path: '/library', label: 'library', icon: 'library-icon' },
    { path: '/scheduler', label: 'planning', icon: 'scheduler-icon' },
    // {path: '/search', label: 'search', icon: 'search-icon'},
  ];
  currentApplicationVersion = environment.appVersion;

  protected readonly environment = environment;

  @HostListener('window:load')
  onLoad(event: Event) {
    const gif = document.querySelector('img');
    this.isLoaded = !environment.enableDesktopMode || gif.complete && gif.naturalHeight !== 0;
    setTimeout(() => {
      if (gif) {
        gif.parentElement.style.display = 'none';
      }
    }, 4200);
  }

  ngOnInit() {
    this.backgroundImage = localStorage.getItem('bgUrl');
    // this.message$.subscribe(message => message && this.snackBar.open(message, 'Close', {duration: 3000}));
    this.isLoggedIn$.subscribe(loggedIn => {
      this.isLoggedIn = loggedIn || environment.enableDesktopMode;
      return !loggedIn && this.router.navigate(['login']);
    });
    this.offlineService.addEvents();
    setTimeout(() => {
      this.pingInterval();
    }, 30000);
    this.currentMedia$.subscribe(res => {
      if (res && res.album && res.album.artwork) {
        this.coverUrl = res.album.artwork;
      } else {
        if (res && res.id) {
          this.coverUrl = `${environment.remoteApi}/${environment.apiVersion}/media/artwork/` + res.id;
        }
      }
    });
  }

  pingInterval() {
    setInterval(() => {
      this.ngRedux.dispatch(APing());
    }, 30000);
  }

  openMiniPlayer() {
    this.createOverlay().detachments().subscribe(_ => this.isMiniPlayerOpen = false);
    this.isMiniPlayerOpen = true;
  }

  createOverlay() {
    const positionStrategy = this.overlay.position()
      .global()
      .centerHorizontally()
      .centerVertically();

    const overlayRef = this.overlay.create({
      backdropClass: 'mini-player-backdrop',
      panelClass: 'mini-player-panel',
      disposeOnNavigation: true,
      hasBackdrop: true,
      positionStrategy,
      height: '100%'
    });

    // Instantiate new WeakMap for our custom injection tokens
    const injectionTokens = new WeakMap();

    // Set custom injection tokens
    injectionTokens.set(overlayRefToken, overlayRef);

    // Instantiate new PortalInjector
    const injector = new PortalInjector(this.injector, injectionTokens);

    // Create ComponentPortal that can be attached to a PortalHost
    const containerPortal = new ComponentPortal(MiniPlayerComponent, null, injector);

    // Attach ComponentPortal to PortalHost
    overlayRef.attach(containerPortal);

    overlayRef.backdropClick().subscribe(_ => overlayRef.detach());

    return overlayRef;
  }

  onActivate(event) {
    const scrollToTop = window.setInterval(() => {
      const pos = window.pageYOffset;
      if (pos > 0) {
        window.scrollTo(0, pos - 20); // how far to scroll on each step
      } else {
        window.clearInterval(scrollToTop);
      }
    }, 16);
  }
}
